import { AccountMetricsChart } from '@/components/Charts/AccountMetricsChart'
import { AdMetricsChart } from '@/components/Charts/AdMetricsChart'
import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  ChevronDown,
  DollarSign,
  Eye,
  Lightbulb,
  MousePointer,
  Percent,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsInsights,
  type GoogleAdsInsightsResult,
} from 'sprites-types/workflow/tools/googleAdsInsights'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

// Priority badge component
function PriorityBadge({ priority }: { priority: 'high' | 'medium' | 'low' }) {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
  }

  const icons = {
    high: '🔴',
    medium: '🟡',
    low: '🟢',
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${styles[priority]}`}
    >
      <span>{icons[priority]}</span>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  )
}

// Category badge component
function CategoryBadge({ category }: { category: string }) {
  const styles: Record<string, string> = {
    Performance: 'bg-blue-100 text-blue-700 border-blue-200',
    Budget: 'bg-purple-100 text-purple-700 border-purple-200',
    Keywords: 'bg-orange-100 text-orange-700 border-orange-200',
    Creative: 'bg-pink-100 text-pink-700 border-pink-200',
  }

  const style = styles[category] || 'bg-gray-100 text-gray-700 border-gray-200'

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {category}
    </span>
  )
}

// Utility functions
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`
}

function GoogleAdsInsightsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({})
  const [expandedChart, setExpandedChart] = useState<string | null>(null)

  // Parse the result using shared schema
  const schema = getToolResultSchema(googleAdsInsights, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  // Handle both emitMode: 'single' and 'multi' (array of results)
  const data = parseResult.data
  const result: GoogleAdsInsightsResult = Array.isArray(data) ? data[0] : data

  const toggleInsight = (index: string) => {
    setExpandedInsights((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  // Group insights by priority
  const highPriorityInsights = result.insights.filter((i) => i.priority === 'high')
  const mediumPriorityInsights = result.insights.filter((i) => i.priority === 'medium')
  const lowPriorityInsights = result.insights.filter((i) => i.priority === 'low')

  // Determine entity context for chart title
  const getEntityContext = ():
    | { type: 'account' | 'campaign' | 'adgroup' | 'assetgroup' | 'ad'; name?: string }
    | undefined => {
    const performanceData = result.performanceData
    if (!performanceData) return undefined

    // Check if focusing on a single entity
    if (performanceData.campaigns?.length === 1) {
      return { type: 'campaign', name: performanceData.campaigns[0].campaign_name }
    }
    if (performanceData.adgroups?.length === 1 && !performanceData.campaigns?.length) {
      return { type: 'adgroup', name: performanceData.adgroups[0].adgroup_name }
    }
    if (performanceData.assetgroups?.length === 1 && !performanceData.campaigns?.length) {
      return { type: 'assetgroup', name: performanceData.assetgroups[0].assetgroup_name }
    }
    if (performanceData.ads?.length === 1 && !performanceData.adgroups?.length) {
      return { type: 'ad', name: performanceData.ads[0].ad_name }
    }

    return { type: 'account' }
  }

  // Calculate aggregated metrics from performance data
  // Only aggregate from the lowest/most granular level to avoid double-counting
  const calculateMetrics = () => {
    const performanceData = result.performanceData
    if (!performanceData) return null

    // Determine which entity level to aggregate from
    // Use the most granular level available to avoid counting the same metrics multiple times
    // Hierarchy: Ads (most granular) > Ad Groups > Campaigns
    // Asset Groups are separate (for Performance Max campaigns)
    let entitiesToAggregate: any[] = []

    if (performanceData.ads && performanceData.ads.length > 0) {
      // Use ads if available (most granular for regular campaigns)
      entitiesToAggregate = performanceData.ads
    } else if (performanceData.adgroups && performanceData.adgroups.length > 0) {
      // Use ad groups if no ads available
      entitiesToAggregate = performanceData.adgroups
    } else if (performanceData.campaigns && performanceData.campaigns.length > 0) {
      // Use campaigns if no ad groups or ads
      entitiesToAggregate = performanceData.campaigns
    }

    // Add asset groups separately (they're from Performance Max campaigns, separate hierarchy)
    if (performanceData.assetgroups && performanceData.assetgroups.length > 0) {
      entitiesToAggregate = [...entitiesToAggregate, ...performanceData.assetgroups]
    }

    if (entitiesToAggregate.length === 0) return null

    const totals = entitiesToAggregate.reduce(
      (acc, entity) => {
        acc.impressions += entity.impressions || 0
        acc.clicks += entity.clicks || 0
        acc.spend += entity.spend || 0
        acc.conversions += entity.conversions || 0
        return acc
      },
      { impressions: 0, clicks: 0, spend: 0, conversions: 0 }
    )

    const avgCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0
    const avgCPC = totals.clicks > 0 ? totals.spend / totals.clicks : 0
    const conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0

    return {
      ...totals,
      avgCTR,
      avgCPC,
      conversionRate,
      campaigns: performanceData.campaigns?.length || 0,
      adgroups: performanceData.adgroups?.length || 0,
      ads: performanceData.ads?.length || 0,
      assetgroups: performanceData.assetgroups?.length || 0,
    }
  }

  const metrics = calculateMetrics()

  return (
    <div className="space-y-3">
      {/* Header */}
      <ContextMenuWrapper actions={[]}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <SiGoogleads className="h-5 w-5 text-[#4285f4]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">
                Google Ads Insights & Recommendations
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {result.insights.length} insights • {highPriorityInsights.length} high priority
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>

          {isExpanded && (
            <div className="border-t border-gray-200/50 px-4 py-4">
              {/* Performance Statistics */}
              {metrics && (
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Performance Statistics</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  {/* Entity Counts */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {metrics.campaigns > 0 && (
                      <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                        {metrics.campaigns} Campaign{metrics.campaigns !== 1 ? 's' : ''}
                      </div>
                    )}
                    {metrics.adgroups > 0 && (
                      <div className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                        {metrics.adgroups} Ad Group{metrics.adgroups !== 1 ? 's' : ''}
                      </div>
                    )}
                    {metrics.ads > 0 && (
                      <div className="rounded-full bg-pink-100 px-3 py-1 text-xs font-medium text-pink-700">
                        {metrics.ads} Ad{metrics.ads !== 1 ? 's' : ''}
                      </div>
                    )}
                    {metrics.assetgroups > 0 && (
                      <div className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                        {metrics.assetgroups} Asset Group{metrics.assetgroups !== 1 ? 's' : ''}
                      </div>
                    )}
                    {result.performanceData?.dateRange && (
                      <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        {result.performanceData.dateRange.start} to{' '}
                        {result.performanceData.dateRange.end}
                      </div>
                    )}
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Eye className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Impressions</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(metrics.impressions)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <MousePointer className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Clicks</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(metrics.clicks)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Total Spend</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(metrics.spend)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Percent className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Avg CTR</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatPercent(metrics.avgCTR)}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Avg CPC</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(metrics.avgCPC)}
                      </div>
                    </div>

                    {metrics.conversions > 0 && (
                      <>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="mb-1 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-gray-600" />
                            <span className="text-xs font-medium text-gray-700">
                              Total Conversions
                            </span>
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatNumber(metrics.conversions)}
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="mb-1 flex items-center gap-2">
                            <Percent className="h-4 w-4 text-gray-600" />
                            <span className="text-xs font-medium text-gray-700">
                              Conversion Rate
                            </span>
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatPercent(metrics.conversionRate)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Executive Summary */}
              <div className="mb-6 overflow-hidden rounded-xl border border-blue-200/60 bg-white shadow-sm">
                <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-blue-50/50 px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#4285f4]">
                      <Lightbulb className="h-4.5 w-4.5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-gray-900">Executive Summary</div>
                      <div className="text-xs text-gray-600">
                        Overview of your Google Ads performance
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-5 py-5">
                  <div className="text-sm leading-relaxed text-gray-800">
                    <EnhancedMarkdown className="prose prose-sm prose-headings:mt-5 prose-headings:mb-3 prose-headings:font-bold prose-headings:text-gray-900 prose-h3:text-base prose-h3:border-b prose-h3:border-gray-200 prose-h3:pb-2 prose-p:my-3 prose-p:leading-relaxed prose-p:text-gray-700 prose-strong:font-semibold prose-strong:text-gray-900 prose-ul:my-3 prose-ul:ml-1 prose-ul:space-y-1.5 prose-li:text-gray-700 prose-li:pl-1 prose-li:leading-relaxed max-w-none [&>h3:first-child]:mt-0">
                      {result.summary}
                    </EnhancedMarkdown>
                  </div>
                  <AccountMetricsChart platform="google" entityContext={getEntityContext()} />
                </div>

                {/* Quick Stats */}
                <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-gray-200/50">
                      <CheckCircle className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-xs font-medium text-gray-700">
                        {result.insights.length} Insights
                      </span>
                    </div>
                    {highPriorityInsights.length > 0 && (
                      <div className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-red-200/50">
                        <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                        <span className="text-xs font-medium text-gray-700">
                          {highPriorityInsights.length} High Priority
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Performance Highlights */}
              {((result.topPerformers && result.topPerformers.length > 0) ||
                (result.underperformers && result.underperformers.length > 0)) && (
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Performance Highlights</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  <div className="space-y-4">
                    {/* Top Performers */}
                    {result.topPerformers && result.topPerformers.length > 0 && (
                      <div className="overflow-hidden rounded-xl border-2 border-green-300 bg-gradient-to-br from-green-50 to-green-100/50 shadow-md">
                        <div className="border-b border-green-200 bg-green-100/80 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg bg-green-600 p-1.5">
                              <TrendingUp className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-green-900">Top Performers</div>
                              <div className="text-xs text-green-700">
                                Strong performance driving results
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3 p-4">
                          {result.topPerformers.map((performer, index) => (
                            <div
                              key={index}
                              className="rounded-lg border border-green-300 bg-white p-3 shadow-sm"
                            >
                              <div className="mb-1.5 flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-xs font-semibold text-gray-900">
                                    {performer.name}
                                  </div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-xs font-bold text-green-700">
                                    {performer.metric}
                                  </div>
                                  <div className="text-xs font-semibold text-green-600">
                                    {typeof performer.value === 'number'
                                      ? performer.value.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })
                                      : performer.value}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs leading-relaxed text-gray-600 [&_p]:text-xs [&_p]:leading-relaxed [&_strong]:font-semibold">
                                {performer.description ? (
                                  <EnhancedMarkdown>{performer.description}</EnhancedMarkdown>
                                ) : (
                                  `Excelling with strong ${performer.metric.toLowerCase()} performance, driving efficient results for the account`
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Underperformers */}
                    {result.underperformers && result.underperformers.length > 0 && (
                      <div className="overflow-hidden rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100/50 shadow-md">
                        <div className="border-b border-orange-200 bg-orange-100/80 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg bg-orange-600 p-1.5">
                              <TrendingDown className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-orange-900">
                                Needs Attention
                              </div>
                              <div className="text-xs text-orange-700">
                                Opportunities for optimization
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3 p-4">
                          {result.underperformers.map((performer, index) => (
                            <div
                              key={index}
                              className="rounded-lg border border-orange-300 bg-white p-3 shadow-sm"
                            >
                              <div className="mb-1.5 flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-xs font-semibold text-gray-900">
                                    {performer.name}
                                  </div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-xs font-bold text-orange-700">
                                    {performer.metric}
                                  </div>
                                  <div className="text-xs font-semibold text-orange-600">
                                    {typeof performer.value === 'number'
                                      ? performer.value.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })
                                      : performer.value}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs leading-relaxed text-gray-600 [&_p]:text-xs [&_p]:leading-relaxed [&_strong]:font-semibold">
                                {performer.description ? (
                                  <EnhancedMarkdown>{performer.description}</EnhancedMarkdown>
                                ) : (
                                  `Underperforming with ${performer.metric.toLowerCase()} below benchmarks, requires attention and optimization`
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Changes */}
              {result.insights.length > 0 && (
                <div>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-900">Suggested Changes</div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  {/* High Priority Insights */}
                  {highPriorityInsights.length > 0 && (
                    <div className="mb-5">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🔴 High Priority
                      </div>
                      <div className="space-y-3">
                        {highPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`high-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`high-${index}`] || false}
                            onToggle={() => toggleInsight(`high-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Medium Priority Insights */}
                  {mediumPriorityInsights.length > 0 && (
                    <div className="mb-5">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🟡 Medium Priority
                      </div>
                      <div className="space-y-3">
                        {mediumPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`medium-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`medium-${index}`] || false}
                            onToggle={() => toggleInsight(`medium-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Low Priority Insights */}
                  {lowPriorityInsights.length > 0 && (
                    <div className="mb-3">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        🟢 Low Priority
                      </div>
                      <div className="space-y-3">
                        {lowPriorityInsights.map((insight, index) => (
                          <InsightCard
                            key={`low-${index}`}
                            insight={insight}
                            isExpanded={expandedInsights[`low-${index}`] || false}
                            onToggle={() => toggleInsight(`low-${index}` as any)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Entity Performance Breakdown */}
              {result.performanceData &&
                (result.performanceData.campaigns?.length ||
                  result.performanceData.adgroups?.length ||
                  result.performanceData.ads?.length ||
                  result.performanceData.assetgroups?.length) && (
                  <div className="mb-6">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="text-sm font-bold text-gray-900">
                        Entity Performance Breakdown
                      </div>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>

                    {/* Campaigns */}
                    {result.performanceData.campaigns &&
                      result.performanceData.campaigns.length > 0 && (
                        <EntitySection
                          title="Campaigns"
                          entities={result.performanceData.campaigns}
                          type="campaign"
                          expandedChart={expandedChart}
                          onChartToggle={setExpandedChart}
                        />
                      )}

                    {/* Ad Groups */}
                    {result.performanceData.adgroups &&
                      result.performanceData.adgroups.length > 0 && (
                        <EntitySection
                          title="Ad Groups"
                          entities={result.performanceData.adgroups}
                          type="adgroup"
                          expandedChart={expandedChart}
                          onChartToggle={setExpandedChart}
                        />
                      )}

                    {/* Ads */}
                    {result.performanceData.ads && result.performanceData.ads.length > 0 && (
                      <EntitySection
                        title="Ads"
                        entities={result.performanceData.ads}
                        type="ad"
                        expandedChart={expandedChart}
                        onChartToggle={setExpandedChart}
                      />
                    )}

                    {/* Asset Groups */}
                    {result.performanceData.assetgroups &&
                      result.performanceData.assetgroups.length > 0 && (
                        <EntitySection
                          title="Asset Groups"
                          entities={result.performanceData.assetgroups}
                          type="assetgroup"
                        />
                      )}
                  </div>
                )}
            </div>
          )}
        </div>
      </ContextMenuWrapper>
    </div>
  )
}

// Individual insight card component
function InsightCard({
  insight,
  isExpanded,
  onToggle,
}: {
  insight: GoogleAdsInsightsResult['insights'][0]
  isExpanded: boolean
  onToggle: () => void
}) {
  // Priority-based styling
  const priorityStyles = {
    high: {
      border: 'border-red-300',
      shadow: 'shadow-md shadow-red-100/50',
      accent: 'border-l-4 border-l-red-500',
      bg: 'bg-red-50/30',
      hoverBg: 'hover:bg-red-50/50',
      icon: 'text-red-600',
      iconBg: 'bg-red-100',
      expandedBg: 'bg-red-50/40',
      expandedBorder: 'border-red-200',
    },
    medium: {
      border: 'border-yellow-300',
      shadow: 'shadow-md shadow-yellow-100/50',
      accent: 'border-l-4 border-l-yellow-500',
      bg: 'bg-yellow-50/30',
      hoverBg: 'hover:bg-yellow-50/50',
      icon: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      expandedBg: 'bg-yellow-50/40',
      expandedBorder: 'border-yellow-200',
    },
    low: {
      border: 'border-green-300',
      shadow: 'shadow shadow-green-100/50',
      accent: 'border-l-4 border-l-green-500',
      bg: 'bg-green-50/20',
      hoverBg: 'hover:bg-green-50/40',
      icon: 'text-green-600',
      iconBg: 'bg-green-100',
      expandedBg: 'bg-green-50/30',
      expandedBorder: 'border-green-200',
    },
  }

  const styles = priorityStyles[insight.priority]

  return (
    <div
      className={`overflow-hidden rounded-2xl ${styles.border} ${styles.shadow} ${styles.accent} ${styles.bg} transition-all duration-200`}
    >
      <button
        onClick={onToggle}
        className={`flex w-full items-start gap-3 p-4 text-left transition-colors ${styles.hoverBg}`}
      >
        <div className="flex-shrink-0 pt-0.5">
          <div className={`rounded-lg p-1.5 ${styles.iconBg}`}>
            {insight.priority === 'high' ? (
              <AlertCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : insight.priority === 'medium' ? (
              <Lightbulb className={`h-4.5 w-4.5 ${styles.icon}`} />
            ) : (
              <CheckCircle className={`h-4.5 w-4.5 ${styles.icon}`} />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <CategoryBadge category={insight.category} />
            <PriorityBadge priority={insight.priority} />
          </div>
          <div className="text-sm font-semibold leading-relaxed text-gray-900">
            <EnhancedMarkdown className="prose prose-sm prose-p:font-semibold prose-p:leading-relaxed max-w-none">
              {insight.finding}
            </EnhancedMarkdown>
          </div>
        </div>
        <div className="flex-shrink-0">
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className={`border-t ${styles.expandedBorder} ${styles.expandedBg} px-4 py-4`}>
          <div className="mb-3 rounded-lg border border-blue-200 bg-white/80 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5">
              <div className="rounded bg-blue-100 px-2 py-0.5">
                <span className="text-xs font-bold text-blue-700">💡 ACTION</span>
              </div>
            </div>
            <div className="text-sm font-medium leading-relaxed text-gray-800">
              <EnhancedMarkdown className="prose prose-sm prose-p:font-medium prose-p:leading-relaxed prose-strong:font-bold prose-strong:text-gray-900 max-w-none">
                {insight.recommendation}
              </EnhancedMarkdown>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white/60 p-3">
            <div className="mb-1.5 text-xs font-semibold text-gray-600">📈 Expected Impact</div>
            <div className="text-sm leading-relaxed text-gray-700">
              <EnhancedMarkdown className="prose prose-sm prose-p:leading-relaxed max-w-none">
                {insight.impact}
              </EnhancedMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Entity Section Component - Shows a collapsible section of entities
function EntitySection({
  title,
  entities,
  type,
  expandedChart,
  onChartToggle,
}: {
  title: string
  entities: any[]
  type: 'campaign' | 'adgroup' | 'ad' | 'assetgroup'
  expandedChart?: string | null
  onChartToggle?: (entityId: string | null) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showNoStats, setShowNoStats] = useState(false)

  const typeColors = {
    campaign: 'bg-blue-100 text-blue-800 border-blue-200',
    adgroup: 'bg-purple-100 text-purple-800 border-purple-200',
    ad: 'bg-pink-100 text-pink-800 border-pink-200',
    assetgroup: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  }

  // Sort entities: those with stats first (sorted by performance), then those without
  const sortedEntities = [...entities].sort((a, b) => {
    const aHasStats = (a.impressions || 0) > 0 || (a.clicks || 0) > 0 || (a.spend || 0) > 0
    const bHasStats = (b.impressions || 0) > 0 || (b.clicks || 0) > 0 || (b.spend || 0) > 0

    // Entities without stats go to the end
    if (aHasStats && !bHasStats) return -1
    if (!aHasStats && bHasStats) return 1
    if (!aHasStats && !bHasStats) return 0

    // For entities with stats, sort by spend (descending) - most important/active first
    // Spend is a reliable, consistent metric that indicates priority
    return (b.spend || 0) - (a.spend || 0)
  })

  // Separate entities with and without stats
  const entitiesWithStats = sortedEntities.filter(
    (e) => (e.impressions || 0) > 0 || (e.clicks || 0) > 0 || (e.spend || 0) > 0
  )
  const entitiesWithoutStats = sortedEntities.filter(
    (e) => (e.impressions || 0) === 0 && (e.clicks || 0) === 0 && (e.spend || 0) === 0
  )

  const visibleEntities = showNoStats ? sortedEntities : entitiesWithStats

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
      >
        <div className="flex items-center gap-3">
          <span
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${typeColors[type]}`}
          >
            {title}
          </span>
          <span className="text-sm font-medium text-gray-700">
            {entitiesWithStats.length} active
            {entitiesWithoutStats.length > 0 && (
              <span className="text-gray-500"> • {entitiesWithoutStats.length} inactive</span>
            )}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="mt-3">
          <div className="space-y-2">
            {visibleEntities.map((entity) => {
              const hasStats =
                (entity.impressions || 0) > 0 || (entity.clicks || 0) > 0 || (entity.spend || 0) > 0
              // Use the appropriate ID field based on entity type
              const entityId =
                type === 'campaign'
                  ? entity.campaign_id
                  : type === 'adgroup'
                    ? entity.adgroup_id
                    : type === 'ad'
                      ? entity.ad_id
                      : entity.assetgroup_id
              return (
                <EntityCard
                  key={entityId || `${type}-${entity.name || Math.random()}`}
                  entity={entity}
                  type={type}
                  hasStats={hasStats}
                  expandedChart={expandedChart}
                  onChartToggle={onChartToggle}
                />
              )
            })}
          </div>

          {entitiesWithoutStats.length > 0 && !showNoStats && (
            <button
              onClick={() => setShowNoStats(true)}
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              Show {entitiesWithoutStats.length} inactive {title.toLowerCase()} with no stats
            </button>
          )}

          {entitiesWithoutStats.length > 0 && showNoStats && (
            <button
              onClick={() => setShowNoStats(false)}
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              Hide {entitiesWithoutStats.length} inactive {title.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Entity Card Component - Shows individual entity performance
function EntityCard({
  entity,
  type,
  hasStats,
  expandedChart,
  onChartToggle,
}: {
  entity: any
  type: 'campaign' | 'adgroup' | 'ad' | 'assetgroup'
  hasStats: boolean
  expandedChart?: string | null
  onChartToggle?: (entityId: string | null) => void
}) {
  const getEntityName = () => {
    switch (type) {
      case 'campaign':
        return entity.campaign_name || entity.name || 'Unnamed Campaign'
      case 'adgroup':
        return entity.adgroup_name || entity.name || 'Unnamed Ad Group'
      case 'ad':
        return entity.ad_name || entity.name || 'Unnamed Ad'
      case 'assetgroup':
        return entity.assetgroup_name || entity.name || 'Unnamed Asset Group'
    }
  }

  const getEntityId = () => {
    switch (type) {
      case 'campaign':
        return entity.campaign_id
      case 'adgroup':
        return entity.adgroup_id
      case 'ad':
        return entity.ad_id
      case 'assetgroup':
        return entity.assetgroup_id
    }
  }

  const getCampaignInfo = () => {
    if (type === 'adgroup' || type === 'ad') {
      return entity.campaign_name
    }
    return null
  }

  return (
    <div
      className={`rounded-lg border border-gray-200 p-4 shadow-sm ${
        hasStats ? 'bg-white' : 'bg-gray-50 opacity-60'
      }`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${hasStats ? 'text-gray-900' : 'text-gray-500'}`}>
            {getEntityName()}
          </div>
          <div className={`mt-0.5 text-xs ${hasStats ? 'text-gray-600' : 'text-gray-400'}`}>
            ID: {getEntityId()}
          </div>
          {getCampaignInfo() && (
            <div className={`mt-0.5 text-xs ${hasStats ? 'text-gray-500' : 'text-gray-400'}`}>
              Campaign: {getCampaignInfo()}
            </div>
          )}
        </div>
        {entity.status && (
          <span
            className={`ml-2 flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium ${
              hasStats ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {entity.status}
          </span>
        )}
      </div>
      {!hasStats && (
        <div className="mb-3 text-xs italic text-gray-500">No performance data available</div>
      )}

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-sm font-bold text-gray-900">
            {formatNumber(entity.impressions || 0)}
          </div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <MousePointer className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Clicks</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatNumber(entity.clicks || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Spend</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.spend || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <Percent className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CTR</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatPercent(entity.ctr || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CPC</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.cpc || 0)}</div>
        </div>

        <div className="rounded-md bg-gray-50 p-2">
          <div className="mb-0.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">CPM</span>
          </div>
          <div className="text-sm font-bold text-gray-900">{formatCurrency(entity.cpm || 0)}</div>
        </div>

        {entity.conversions != null && entity.conversions > 0 && (
          <>
            <div className="rounded-md bg-gray-50 p-2">
              <div className="mb-0.5 flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Conversions</span>
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatNumber(entity.conversions)}
              </div>
            </div>

            <div className="rounded-md bg-gray-50 p-2">
              <div className="mb-0.5 flex items-center gap-1">
                <Percent className="h-3 w-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Conv. Rate</span>
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatPercent(entity.conversion_rate || 0)}
              </div>
            </div>
          </>
        )}

        {entity.impression_share != null && (
          <div className="rounded-md bg-gray-50 p-2">
            <div className="mb-0.5 flex items-center gap-1">
              <Percent className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Impr. Share</span>
            </div>
            <div className="text-sm font-bold text-gray-900">
              {formatPercent(entity.impression_share)}
            </div>
          </div>
        )}
      </div>

      {/* Chart Button (for all entity types with stats) */}
      {hasStats && onChartToggle && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <button
            onClick={() => {
              const entityId = getEntityId()
              if (entityId) {
                onChartToggle(expandedChart === entityId ? null : entityId)
              }
            }}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <BarChart3 className="h-4 w-4" />
            {expandedChart === getEntityId() ? 'Hide Chart' : 'View Performance Chart'}
          </button>
        </div>
      )}

      {/* Chart Display */}
      {expandedChart === getEntityId() && (
        <AdMetricsChart
          entityId={getEntityId() || ''}
          entityName={getEntityName()}
          entityType={type === 'campaign' ? 'campaign' : type === 'adgroup' ? 'adset' : 'ad'}
          platform="google"
        />
      )}
    </div>
  )
}

function downloadGoogleAdsInsights(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsInsightsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsInsights, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  // Handle both emitMode: 'single' and 'multi' (array of results)
  const data = parseResult.data
  const result: GoogleAdsInsightsResult = Array.isArray(data) ? data[0] : data

  let markdown = '# Google Ads Insights & Recommendations\n\n'

  // Summary
  markdown += '## Summary\n\n'
  markdown += `${result.summary}\n\n`

  // Insights by priority
  const highPriority = result.insights.filter((i) => i.priority === 'high')
  const mediumPriority = result.insights.filter((i) => i.priority === 'medium')
  const lowPriority = result.insights.filter((i) => i.priority === 'low')

  if (highPriority.length > 0) {
    markdown += '## 🔴 High Priority Actions\n\n'
    highPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  if (mediumPriority.length > 0) {
    markdown += '## 🟡 Medium Priority Actions\n\n'
    mediumPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  if (lowPriority.length > 0) {
    markdown += '## 🟢 Low Priority Actions\n\n'
    lowPriority.forEach((insight, index) => {
      markdown += `### ${index + 1}. ${insight.category}\n\n`
      markdown += `**Finding:** ${insight.finding}\n\n`
      markdown += `**Recommendation:** ${insight.recommendation}\n\n`
      markdown += `**Impact:** ${insight.impact}\n\n`
    })
  }

  // Top Performers
  if (result.topPerformers && result.topPerformers.length > 0) {
    markdown += '## ✅ Top Performers\n\n'
    result.topPerformers.forEach((performer) => {
      markdown += `- **${performer.name}** (${performer.id}): ${performer.metric} = ${performer.value}\n`
    })
    markdown += '\n'
  }

  // Underperformers
  if (result.underperformers && result.underperformers.length > 0) {
    markdown += '## ⚠️ Needs Attention\n\n'
    result.underperformers.forEach((performer) => {
      markdown += `- **${performer.name}** (${performer.id}): ${performer.metric} = ${performer.value}\n`
    })
    markdown += '\n'
  }

  // Entity Performance Breakdown
  if (result.performanceData) {
    markdown += '## Entity Performance Breakdown\n\n'

    // Campaigns
    if (result.performanceData.campaigns && result.performanceData.campaigns.length > 0) {
      markdown += '### Campaigns\n\n'
      result.performanceData.campaigns.forEach((campaign) => {
        markdown += `**${campaign.campaign_name || 'Unnamed'}** (ID: ${campaign.campaign_id})\n`
        markdown += `- Impressions: ${(campaign.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(campaign.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(campaign.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(campaign.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(campaign.cpc || 0).toFixed(2)}\n`
        markdown += `- CPM: $${(campaign.cpm || 0).toFixed(2)}\n`
        if (campaign.conversions != null && campaign.conversions > 0) {
          markdown += `- Conversions: ${campaign.conversions} (${(campaign.conversion_rate || 0).toFixed(2)}%)\n`
        }
        markdown += '\n'
      })
    }

    // Ad Groups
    if (result.performanceData.adgroups && result.performanceData.adgroups.length > 0) {
      markdown += '### Ad Groups\n\n'
      result.performanceData.adgroups.forEach((adgroup) => {
        markdown += `**${adgroup.adgroup_name || 'Unnamed'}** (ID: ${adgroup.adgroup_id})\n`
        markdown += `- Campaign: ${adgroup.campaign_name || 'N/A'}\n`
        markdown += `- Impressions: ${(adgroup.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(adgroup.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(adgroup.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(adgroup.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(adgroup.cpc || 0).toFixed(2)}\n`
        if (adgroup.conversions != null && adgroup.conversions > 0) {
          markdown += `- Conversions: ${adgroup.conversions} (${(adgroup.conversion_rate || 0).toFixed(2)}%)\n`
        }
        markdown += '\n'
      })
    }

    // Ads
    if (result.performanceData.ads && result.performanceData.ads.length > 0) {
      markdown += '### Ads\n\n'
      result.performanceData.ads.forEach((ad) => {
        markdown += `**${ad.ad_name || 'Unnamed'}** (ID: ${ad.ad_id})\n`
        markdown += `- Campaign: ${ad.campaign_name || 'N/A'}\n`
        markdown += `- Ad Group: ${ad.adgroup_name || 'N/A'}\n`
        markdown += `- Impressions: ${(ad.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(ad.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(ad.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(ad.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(ad.cpc || 0).toFixed(2)}\n`
        if (ad.conversions != null && ad.conversions > 0) {
          markdown += `- Conversions: ${ad.conversions} (${(ad.conversion_rate || 0).toFixed(2)}%)\n`
        }
        markdown += '\n'
      })
    }

    // Asset Groups
    if (result.performanceData.assetgroups && result.performanceData.assetgroups.length > 0) {
      markdown += '### Asset Groups\n\n'
      result.performanceData.assetgroups.forEach((assetgroup) => {
        markdown += `**${assetgroup.assetgroup_name || 'Unnamed'}** (ID: ${assetgroup.assetgroup_id})\n`
        markdown += `- Campaign: ${assetgroup.campaign_name || 'N/A'}\n`
        markdown += `- Impressions: ${(assetgroup.impressions || 0).toLocaleString()}\n`
        markdown += `- Clicks: ${(assetgroup.clicks || 0).toLocaleString()}\n`
        markdown += `- Spend: $${(assetgroup.spend || 0).toFixed(2)}\n`
        markdown += `- CTR: ${(assetgroup.ctr || 0).toFixed(2)}%\n`
        markdown += `- CPC: $${(assetgroup.cpc || 0).toFixed(2)}\n`
        if (assetgroup.conversions != null && assetgroup.conversions > 0) {
          markdown += `- Conversions: ${assetgroup.conversions} (${(assetgroup.conversion_rate || 0).toFixed(2)}%)\n`
        }
        markdown += '\n'
      })
    }
  }

  return markdown
}

export const GoogleAdsInsightsDisplay = createWorkflowToolComponent(
  GoogleAdsInsightsDisplayComponent,
  downloadGoogleAdsInsights,
  googleAdsInsightsToMarkdown
)
